import React from 'react';
import classNames from 'classnames';

import Icon from 'buyside/components/presentation/Icon/icon';
import Link from 'buyside/components/presentation/Link/link';
import {
  IdeaResearchItemProps,
  ResearchTypes,
  SharedResearchItemProps,
  WhitePaperResearchItemProps
} from 'buyside/components/presentation/ResearchItem/researchItem';

import { getExtensionIcon } from 'buyside/util/fileHelper';
import {
  ideaSearchUrl,
  ideaUrl,
  whitePaperSearchUrl,
  whitePaperShowUrl
} from 'buyside/util/urlHelper';

import styles from './researchItemBodyStyles.scss';

type SharedResearchItemBodyProps = Pick<SharedResearchItemProps, 'narrow'>;

type IdeaResearchItemBodyProps = Pick<
  IdeaResearchItemProps,
  'id' | 'attachmentExtensions' | 'elevatorPitch' | 'tagList'
> &
  SharedResearchItemBodyProps & {
    researchType: ResearchTypes.IDEA;
  };

type WhitePaperResearchItemBodyProps = Pick<
  WhitePaperResearchItemProps,
  'id' | 'attachmentExtensions' | 'elevatorPitch' | 'tagList'
> &
  SharedResearchItemBodyProps & {
    researchType: ResearchTypes.WHITE_PAPER;
    subcategories: {
      label: string;
      categoryNames:
        | WhitePaperResearchItemProps['esgNames']
        | WhitePaperResearchItemProps['geographyNames']
        | WhitePaperResearchItemProps['globalMacroNames']
        | WhitePaperResearchItemProps['sectorNames'];
    }[];
  };

const ResearchItemBody: React.FC<
  IdeaResearchItemBodyProps | WhitePaperResearchItemBodyProps
> = props => {
  const { id, attachmentExtensions, elevatorPitch, researchType, narrow, tagList } = props;

  const isWhitePaper = researchType === ResearchTypes.WHITE_PAPER;

  return (
    <section className={classNames(styles.researchItemBody, { [styles.narrow]: narrow })}>
      {isWhitePaper ? (
        <div className={styles.researchSubcategories}>
          {props.subcategories.map(({ label, categoryNames }) =>
            categoryNames ? (
              <span key={label}>
                <strong>{label}</strong>
                {categoryNames}
              </span>
            ) : null
          )}
        </div>
      ) : null}
      {elevatorPitch ? (
        <Link
          className={styles.researchElevatorPitch}
          to={isWhitePaper ? whitePaperShowUrl(id) : ideaUrl(id)}
        >
          {elevatorPitch}
        </Link>
      ) : null}
      {!narrow && attachmentExtensions?.length ? (
        <div className={styles.researchAttachmentArea}>
          {attachmentExtensions.slice(0, 3).map((extention, idx) => (
            <div data-testid="attachment-extension" key={`${extention}-${idx}`}>
              <Icon style={styles.researchAttachmentIcon} type={getExtensionIcon(extention)} />
            </div>
          ))}
          {attachmentExtensions.length > 3 ? '+' : ''}
        </div>
      ) : null}
      {tagList.length ? (
        <div className={styles.researchTagList}>
          {tagList.map((hashTag, idx) => (
            <Link
              key={`${hashTag}-${idx}`}
              to={
                isWhitePaper
                  ? whitePaperSearchUrl(`tag=${hashTag}`)
                  : ideaSearchUrl(`tag=${hashTag}`)
              }
            >
              #{hashTag}
            </Link>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default ResearchItemBody;
